<template>
  <!-- h-100 and overflow-hidden lock the main layout down to the container's bound -->
  <div class="container-fluid d-flex flex-column h-100 overflow-hidden p-3 py-4">
    <h2 class="mb-4 flex-shrink-0">PSAP Simulator</h2>
    
    <!-- Row expands to consume all remaining height; nested items are forced to follow -->
    <div class="row flex-grow-1 overflow-hidden g-4">
      
      <!-- Sent MSD Alerts Panel -->
      <div class="col-md-6 h-100 overflow-hidden">
        <div class="card shadow-sm h-100 d-flex flex-column overflow-hidden">
          <div class="card-header bg-primary text-white flex-shrink-0">
            Sent MSD Alerts
          </div>
          <div class="card-body d-flex flex-column overflow-hidden p-0">
            <ul ref="sentList" class="list-group list-group-flush flex-grow-1 overflow-y-auto m-0 p-3">
              <li v-if="sentAlerts.length === 0" class="list-group-item text-muted border-0 bg-transparent">
                No sent alerts.
              </li>
              <li v-for="(alert, index) in sentAlerts" :key="index" class="list-group-item px-0">
                <strong>Time:</strong> {{ alert.time }}<br>
                <strong>Message:</strong> {{ decodeData(alert.data) || alert.message }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Received MSD Alerts Panel -->
      <div class="col-md-6 h-100 overflow-hidden">
        <div class="card shadow-sm h-100 d-flex flex-column overflow-hidden">
          <div class="card-header bg-success text-white flex-shrink-0">
            Received MSD Alerts from Vehicles
          </div>
          <div class="card-body d-flex flex-column overflow-hidden p-0">
            <ul ref="receivedList" class="list-group list-group-flush flex-grow-1 overflow-y-auto m-0 p-3">
              <li v-if="receivedAlerts.length === 0" class="list-group-item text-muted border-0 bg-transparent">
                No received alerts.
              </li>
              <li v-for="(alert, index) in receivedAlerts" :key="index" class="list-group-item px-0">
                <strong>Time:</strong> {{ alert.time }}<br>
                <strong>VIN:</strong> {{ alert.vin }}<br>
                <strong>IMSI:</strong> {{ alert.imsi }}<br>
                <strong>Location:</strong> {{ formatLocation(alert.location) }}<br>
                <strong>Data:</strong> {{ decodeData(alert.data) }}
              </li>
            </ul>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';

// Define Props (Fixed: object changed to array to fix type validation matches)
const props = defineProps({
  ecallMessages: {
    type: Array,
    default: () => []
  }
});

// Template Refs replacing the old options API this.$refs mechanism
const sentList = ref(null);
const receivedList = ref(null);

// Methods / Helper Functions
const decodeData = (data) => {
  if (data && data.type === 'Buffer' && Array.isArray(data.data)) {
    return String.fromCharCode(...data.data);
  }
  if (data instanceof Uint8Array) {
    return new TextDecoder('ascii').decode(data);
  }
  return data;
};

const formatLocation = (loc) => {
  if (!loc) return '';
  return `${loc.latitude}, ${loc.longitude}`;
};

const scrollToBottom = (elementRef) => {
  if (elementRef.value) {
    elementRef.value.scrollTop = elementRef.value.scrollHeight;
  }
};

// Computed Properties
const sentAlerts = computed(() => {
  return props.ecallMessages
    .filter(msg => msg.sender === 'PSAP')
    .sort((a, b) => new Date(a.time) - new Date(b.time));
});

const receivedAlerts = computed(() => {
  return props.ecallMessages
    // Bug Fix: corrected "new DataTransfer()" to "new Date()" to stop query crashes
    .sort((a, b) => new Date(a.time) - new Date(b.time))
    .map(msg => ({ ...msg, decodeData: decodeData(msg.data) }));
});

// Watchers for Autoscroll Behavior
watch(sentAlerts, () => {
  nextTick(() => scrollToBottom(sentList));
}, { deep: true });

watch(receivedAlerts, () => {
  nextTick(() => scrollToBottom(receivedList));
}, { deep: true });
</script>

<style scoped>
/* 
  No custom pixel heights needed anymore! 
  The lists will fluidly expand to the bottom of the layout pane container.
*/
.row {
  min-height: 0; /* Important fix allowing flex items to safely shrink smaller than layout text contents */
}
</style>
